import './index.styl';
import template from './index.jst';
import _ from 'lodash';
import { Model } from 'backbone';
import { View } from 'backbone.marionette';
import Radio from 'backbone.radio';
import { isMobile, isTablet, Preloader } from '../../../../utils';

import {
	TeamBuilder,
	TradePlayerPopup,
	TradeReversePopup
} from '../../index';
import { isAuthorized } from "../../../../../../core/utils";
import { User } from "../../../../models";
const
	CHANNEL_TRADE = Radio.channel('trade'),
	silent = { silent: true };

/**
 * @description Main Trade View
 * Aggregates all events from child views.
 * Request all necessary data and delegate to work with it all child views.
 */
export class TradeMain extends View {

	get template() {
		return template;
	}

	get className() {
		return 'page-trades';
	}

	regions() {
		return {
			info: '.trade-info-bar',
			team: '.trade-aside',
			controls: '.trade-controls-board',
			pool: '.trade-pool',
			popup: '.trade-player-popup',
			popup_reverse: '.trade-reverse-popup',
			history: '.trade-history',
			ad_container: '.ad-container'
		}
	}

	childViewEvents() {
		return {
			'show:reverse:popup': 'onShowReversePopup',
			'player:show:popup': 'onPlayerShowPopup',
			'player:favourite': 'onPlayerFavourite',
			'trade:out:player': 'onTradeOut'
		}
	}

	initialize() {
		this.model.set({
			substitute: [],
			is_mobile_view: isMobile() && !isTablet()
		});

		this.options.history.clear().setTradeHistoryUrl();

		this.initListeners()
			.requestData();
	}

	onTradeOut({ player_id, ...other }) {
		const player = this.options.players.get(player_id).toJSON();
		CHANNEL_TRADE.trigger('trade:out:player', { player, ...other });
	}

	initListeners() {
		this.listenTo(CHANNEL_TRADE, 'success:trade', this.onSuccessTrade);
		this.listenTo(CHANNEL_TRADE, 'remove:trade:out:player', this.onTradeOutRemove);
		this.listenTo(this, 'data:requested', this.onDataRequested);
		this.listenTo(this.model, 'update', this.getViewParams);

		return this;
	}

	async onSuccessTrade({ team }) {
		Preloader.show();

		await Promise.all([
			this.options.trades.clear().fetch(),
			this.options.history.clear().fetch(),
		]);

		this.options.team.set(team, silent);

		this.trigger('data:requested');

		Preloader.hide();
	}

	get requestModels() {
		const isCoachEnabled = User.attributes.assistant_coach === 1;
		return _.pick(
			this.options,
			'squads', 'rounds', 'players',
			'team', 'favourites', (isCoachEnabled ? 'coach' : null),
			'trades', 'venues', 'history'
		)
	}

	async requestData() {
		await TeamBuilder.prototype.requestData.call(this);

		if (this.options.rounds.is_endseason) {
			return this.trigger('end:season');
		}

		if (this.model.get('is_unlimited_trades')) {
			this.trigger('access:denied', {
				round_id: this.model.get('actual_round_id')
			});
		}
	}

	getDoubleScoreLeaderId() {
		return TeamBuilder.prototype.getDoubleScoreLeaderId.call(this);
	}

	onDataRequested() {
		return TeamBuilder.prototype.onDataRequested.call(this);
	}

	getViewParams() {
		const params = TeamBuilder.prototype.getViewParams.call(this);

		params.trades = this.options.trades.getWorkableRepresentation(params.players_by_id);
		params.trades.length = params.team.week_trades_limit;

		params.transformed_team = this.options.team
			.clone(params.team)
			.transformPlayersIdToObjects({ players_by_id: params.players_by_id })
			.toJSON();

		params.history = this.options.history.toJSON();

		CHANNEL_TRADE.trigger('data:changed', params);
		if (this.options.player_id) {
			CHANNEL_TRADE.trigger('check:player', this.options.player_id);
			this.options.player_id = false;
		}
	}

	onTradeOutRemove({ stay_in_trade }) {
		const
			{ team, players_by_id } = TeamBuilder.prototype.getViewParams.call(this),
			transformed_team = this.options.team
				.clone(team)
				.transformPlayersIdToObjects({ players_by_id })
				.toJSON();

		CHANNEL_TRADE.trigger('team:changed', {
			team: transformed_team,
			stay_in_trade
		});
	}

	onPlayerShowPopup(args) {
		TeamBuilder.prototype.onPlayerShowPopup.call(this, args);
	}

	getPlayerPopupView(args) {
		args.is_authorized = isAuthorized();

		return new TradePlayerPopup({
			model: new Model(args)
		});
	}

	onShowReversePopup(args) {
		const model = this.options.trades.clone();

		model.set(args);

		this.showChildView('popup_reverse', new TradeReversePopup({ model }));
	}

	onPlayerFavourite(args) {
		TeamBuilder.prototype.onPlayerFavourite.call(this, args);
	}

	getMappedPlayers() {
		return TeamBuilder.prototype.getMappedPlayers.call(this, ...arguments);
	}

	getRemainingSalary() {
		return TeamBuilder.prototype.getRemainingSalary.call(this, ...arguments);
	}

	getTeamProjectedScore() {
		return TeamBuilder.prototype.getTeamProjectedScore.call(this, ...arguments);
	}

	updateNotCountedPlayers() {
		return TeamBuilder.prototype.updateNotCountedPlayers.call(this, ...arguments);
	}
}

export default TradeMain;